// MarkdownEditor.tsx — toolbar-driven textarea for the markdown-subset
// description/comment fields, with a live-preview toggle. NOT a contenteditable
// WYSIWYG (repo DNA: hand-rolled, no heavy deps) — every toolbar button just
// inserts/wraps plain markdown-subset tokens into the textarea value.
//
// Shared by SubmitFeedbackPanel (full toolbar, `lightToolbar={false}`) and
// comment composers (`lightToolbar` — no image button, per spec).
import { useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { IconButton } from '@/ui';
import { Icon } from '@/icons';
import { EmojiPicker } from '@/features/home/composer/EmojiPicker';
import { FeedbackMarkdown } from './lib/markdown';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-bottom: none;
  border-radius: ${({ theme }) => theme.radius.md} ${({ theme }) => theme.radius.md} 0 0;
  background: ${({ theme }) => theme.color.glassBg};
`;

const ToolbarDivider = styled.span`
  width: 1px;
  align-self: stretch;
  margin: 4px 2px;
  background: ${({ theme }) => theme.color.glassBorder};
`;

const PreviewToggle = styled.button<{ $on: boolean }>`
  margin-left: auto;
  padding: 4px 10px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  border: 1px solid ${({ theme, $on }) => ($on ? 'transparent' : theme.color.glassBorder)};
  background: ${({ theme, $on }) => ($on ? `${theme.color.brandFrom}22` : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.brandFrom : theme.color.textPrimary)};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 10px 12px;
  resize: vertical;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: 0 0 ${({ theme }) => theme.radius.md} ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

const PreviewBox = styled.div`
  min-height: 120px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: 0 0 ${({ theme }) => theme.radius.md} ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
`;

const EmptyPreview = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
`;

const CharCount = styled.span<{ $near: boolean }>`
  display: block;
  text-align: right;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme, $near }) => ($near ? theme.color.danger : theme.color.textMuted)};
`;

const ErrorText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.danger};
`;

interface Selection {
  next: string;
  selStart: number;
  selEnd: number;
}

/** Wraps the current selection (or a placeholder, if nothing selected) with
 * `before`/`after`. Exported for direct unit testing without a DOM textarea. */
export function wrapSelection(
  value: string,
  start: number,
  end: number,
  before: string,
  after: string,
  placeholder: string,
): Selection {
  const hasSelection = end > start;
  const selected = hasSelection ? value.slice(start, end) : placeholder;
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  return {
    next,
    selStart: start + before.length,
    selEnd: start + before.length + selected.length,
  };
}

/** Prefixes every line the selection touches (or just the current line, if
 * nothing is selected) — used for bullet/numbered lists. `prefixFor(i)` gets
 * the zero-based line index within the touched range, so numbered lists can
 * count "1. ", "2. ", … */
export function prefixLines(
  value: string,
  start: number,
  end: number,
  prefixFor: (i: number) => string,
): Selection {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  let lineEnd = value.indexOf('\n', end);
  if (lineEnd === -1) lineEnd = value.length;
  const block = value.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const prefixed = lines.map((l, i) => `${prefixFor(i)}${l}`).join('\n');
  const next = value.slice(0, lineStart) + prefixed + value.slice(lineEnd);
  return { next, selStart: lineStart, selEnd: lineStart + prefixed.length };
}

export interface MarkdownEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  /** No image button — used for comments. */
  lightToolbar?: boolean;
  /** Uploads a picked file and resolves the markdown-ready {url, alt}, or
   * null on failure (the editor shows a generic inline error either way —
   * pass a rejection or null, both are handled the same). Omit entirely to
   * disable the image button even on a full toolbar. */
  onInsertImage?: (file: File) => Promise<{ url: string; alt: string } | null>;
  disabled?: boolean;
}

export function MarkdownEditor({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  rows = 6,
  lightToolbar = false,
  onInsertImage,
  disabled = false,
}: MarkdownEditorProps) {
  const [previewing, setPreviewing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function apply(sel: Selection) {
    onChange(sel.next);
    const el = taRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(sel.selStart, sel.selEnd);
    });
  }

  function withSelection(fn: (value: string, start: number, end: number) => Selection) {
    const el = taRef.current;
    if (!el) return;
    apply(fn(value, el.selectionStart, el.selectionEnd));
  }

  function insertAtCursor(text: string) {
    const el = taRef.current;
    const pos = el?.selectionStart ?? value.length;
    const next = value.slice(0, pos) + text + value.slice(pos);
    onChange(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(pos + text.length, pos + text.length);
    });
  }

  function handleBold() {
    withSelection((v, s, e) => wrapSelection(v, s, e, '**', '**', 'bold text'));
  }
  function handleItalic() {
    withSelection((v, s, e) => wrapSelection(v, s, e, '*', '*', 'italic text'));
  }
  function handleInlineCode() {
    withSelection((v, s, e) => wrapSelection(v, s, e, '`', '`', 'code'));
  }
  function handleCodeBlock() {
    withSelection((v, s, e) => wrapSelection(v, s, e, '```\n', '\n```', 'code'));
  }
  function handleBulletList() {
    withSelection((v, s, e) => prefixLines(v, s, e, () => '- '));
  }
  function handleNumberedList() {
    withSelection((v, s, e) => prefixLines(v, s, e, (i) => `${i + 1}. `));
  }
  function handleLink() {
    withSelection((v, s, e) => wrapSelection(v, s, e, '[', '](https://)', 'link text'));
  }

  function handlePickImage() {
    setImageError('');
    fileInputRef.current?.click();
  }

  async function handleImageFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onInsertImage) return;
    setUploadingImage(true);
    setImageError('');
    try {
      const result = await onInsertImage(file);
      if (result) {
        insertAtCursor(`![${result.alt}](${result.url})`);
      } else {
        setImageError('Could not upload that image. Please try again.');
      }
    } catch (err) {
      // Distinct failure reasons (at the attachment cap, oversized, or a
      // real upload/network failure) surface their own specific message
      // (2026-08-10 second adversarial review, finding W5) — onInsertImage
      // throws an Error carrying that exact text; anything else falls back
      // to the generic message.
      setImageError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not upload that image. Please try again.',
      );
    } finally {
      setUploadingImage(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (e.key === 'b') {
      e.preventDefault();
      handleBold();
    } else if (e.key === 'i') {
      e.preventDefault();
      handleItalic();
    }
  }

  const showImageButton = !lightToolbar && !!onInsertImage;

  return (
    <Wrap>
      <Label>{label}</Label>
      <Toolbar role="toolbar" aria-label={`${label} formatting`}>
        <IconButton icon="bold" label="Bold" size={15} onClick={handleBold} disabled={disabled} />
        <IconButton
          icon="italic"
          label="Italic"
          size={15}
          onClick={handleItalic}
          disabled={disabled}
        />
        <ToolbarDivider aria-hidden="true" />
        <IconButton
          icon="list"
          label="Bullet list"
          size={15}
          onClick={handleBulletList}
          disabled={disabled}
        />
        <IconButton
          icon="list-ordered"
          label="Numbered list"
          size={15}
          onClick={handleNumberedList}
          disabled={disabled}
        />
        <ToolbarDivider aria-hidden="true" />
        <IconButton
          icon="code"
          label="Inline code"
          size={15}
          onClick={handleInlineCode}
          disabled={disabled}
        />
        <IconButton
          icon="code"
          label="Code block"
          size={15}
          onClick={handleCodeBlock}
          disabled={disabled}
        />
        <IconButton icon="link" label="Link" size={15} onClick={handleLink} disabled={disabled} />
        <EmojiPicker onSelect={insertAtCursor} />
        {showImageButton && (
          <IconButton
            icon="image"
            label={uploadingImage ? 'Uploading image…' : 'Insert image'}
            size={15}
            onClick={handlePickImage}
            disabled={disabled || uploadingImage}
          />
        )}
        <PreviewToggle
          type="button"
          $on={previewing}
          aria-pressed={previewing}
          onClick={() => setPreviewing((p) => !p)}
        >
          <Icon name={previewing ? 'edit' : 'eye'} size={12} /> {previewing ? 'Write' : 'Preview'}
        </PreviewToggle>
      </Toolbar>

      {previewing ? (
        <PreviewBox data-testid="markdown-preview">
          {value.trim() ? (
            <FeedbackMarkdown source={value} />
          ) : (
            <EmptyPreview>Nothing to preview yet.</EmptyPreview>
          )}
        </PreviewBox>
      ) : (
        <TextArea
          ref={taRef}
          aria-label={label}
          value={value}
          maxLength={maxLength}
          rows={rows}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      )}

      {showImageButton && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          hidden
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => void handleImageFile(e)}
        />
      )}
      {imageError && <ErrorText role="alert">{imageError}</ErrorText>}

      {maxLength !== undefined && (
        <CharCount $near={value.length >= maxLength - 200}>
          {value.length}/{maxLength}
        </CharCount>
      )}
    </Wrap>
  );
}
